/**
 * The guest funnel's client-side record. The server owns the guest *session*
 * (cookie `cj_guest`, `POST/PATCH /v1/guest/session`), the upload and the
 * analysis; this only remembers which analysis to poll and what the user
 * chose, so the funnel survives a refresh. sessionStorage by decision:
 * restart-clean on a new visit, nothing lost mid-journey.
 */
import type { TemplateId } from '../api/types';
import { formatBytes } from './viewModel';

const KEY = 'cj_guest_flow_v2'; // `flows.mjs` reads this
const SCHEMA = 2;

export interface GuestUploadMeta {
  uploadId: string;
  name: string;
  sizeBytes: number;
  kind: string;
}
/**
 * The report hero's text slots, each marked `data-hero-slot` in the prototype,
 * in both the landed hero and its skeleton (resume-360 ticket 21). `chip` is
 * the Band chip ("Watch · Data Analyst", or "Not scored · …"), which names the
 * role the visitor typed, so a long one wraps it (ticket 21's review).
 * `flows.mjs` reads this list.
 */
export const HERO_SLOTS = ['chip', 'verdict', 'gap', 'lead', 'title', 'quote', 'ctaHead', 'ctaSub'] as const;
export type HeroSlot = (typeof HERO_SLOTS)[number];
/**
 * The shape the report hero last landed in, so that a reload's skeleton draws
 * that shape and not a guess (resume-360 ticket 21; features/funnel/heroShape.ts).
 * It holds whether the hero had a #1 fix, the line count of each text slot
 * (clamped to heroShape.ts's `MAX_LINES`), and the viewport width it drew them
 * at. It is a flag, counts and a width, never résumé text.
 */
export interface HeroShape {
  width: number;
  topFix: boolean;
  lines: Record<HeroSlot, number>;
}
export interface GuestFlow {
  schemaVersion: number;
  answers: string[];
  upload: GuestUploadMeta | null;
  /**
   * Uploads this visit made and then let go of: replaced by another file,
   * dropped for the sample, forgotten after a failed analysis. They are still
   * on the server until the 7-day purge, so "Delete my upload" deletes them too
   * (legal-pages ticket 04). `patchFlow` keeps the list; nothing else writes it.
   */
  supersededUploadIds: string[];
  analysisId: string | null;
  /** When the analysis on `analysisId` started, ISO — the basis for the
   * 2-minute timeout (ticket 37). Set alongside `analysisId`; bumped to now
   * on a timeout retry so the window restarts without a new analysis. */
  analysisStartedAt: string | null;
  isSampleData: boolean;
  selectedTemplateId: TemplateId | null;
  /** Cached last score, only so Auth can say "your 74 score" without a request. */
  lastScore: number | null;
  /** The last landed report hero's shape. It is forgotten with `lastScore` (`FORGET_REPORT`). */
  heroShape: HeroShape | null;
  createdAt: string | null;
  stale?: boolean;
}

export const EMPTY_FLOW: GuestFlow = {
  schemaVersion: SCHEMA,
  answers: [],
  upload: null,
  supersededUploadIds: [],
  analysisId: null,
  analysisStartedAt: null,
  isSampleData: false,
  selectedTemplateId: null,
  lastScore: null,
  heroShape: null,
  createdAt: null,
};

/**
 * What the flow caches about the report on file, forgotten together whenever
 * that report stops being the one on file: a new upload, a new analysis of the
 * same upload, the sample (from /start or from the analyzing screen's error),
 * a cleared upload, a failed retry, going back to upload. Every such switch
 * spreads this, so the rule lives here and nowhere else; the next report to
 * land writes both again. A record saved before `heroShape` existed reads it
 * as null, so the schema is unchanged.
 */
export const FORGET_REPORT = { lastScore: null, heroShape: null } as const satisfies Partial<GuestFlow>;

function store(): Storage | null {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

export function readFlow(): GuestFlow {
  const s = store();
  if (!s) return { ...EMPTY_FLOW };
  try {
    const raw = s.getItem(KEY);
    if (!raw) return { ...EMPTY_FLOW };
    const parsed = JSON.parse(raw);
    if (parsed.schemaVersion !== SCHEMA) return { ...EMPTY_FLOW, stale: true };
    return { ...EMPTY_FLOW, ...parsed };
  } catch {
    return { ...EMPTY_FLOW, stale: true };
  }
}

/** More than a visit will make (the server takes 10 uploads an hour); a bound, so the record cannot grow without end. */
const SUPERSEDED_MAX = 20;

export function patchFlow(patch: Partial<GuestFlow>): GuestFlow {
  const current = readFlow();
  const next: GuestFlow = { ...current, ...patch, schemaVersion: SCHEMA };
  // Whatever lets go of an upload (a new file, `upload: null`), from whichever
  // screen: its id is kept, here and nowhere else, so no caller can forget to.
  const dropped = current.upload && 'upload' in patch && patch.upload?.uploadId !== current.upload.uploadId ? current.upload.uploadId : null;
  if (dropped && !('supersededUploadIds' in patch)) {
    next.supersededUploadIds = [...(current.supersededUploadIds ?? []).filter((id) => id !== dropped), dropped].slice(-SUPERSEDED_MAX);
  }
  if (!next.createdAt) next.createdAt = new Date().toISOString();
  delete next.stale;
  const s = store();
  if (s) {
    try {
      s.setItem(KEY, JSON.stringify(next));
    } catch {
      /* quota / private mode: the funnel still works for this page */
    }
  }
  return next;
}

export function clearFlow(): void {
  const s = store();
  if (s) {
    try {
      s.removeItem(KEY);
    } catch {
      /* ignore */
    }
  }
}

/* ---- intake ---- */
export const ROLE_OPTIONS = ['Software Engineer', 'Product Manager', 'Product Designer', 'Data Analyst', 'Data Scientist', 'Marketing Manager', 'Sales Representative', 'Account Executive', 'Financial Analyst', 'Operations Manager', 'Customer Success Manager', 'HR Manager', 'Project Manager', 'DevOps Engineer', 'UX Researcher'];
/**
 * What the funnel shows in the hero pill when the one question is skipped. Never
 * stored or sent as the answer (a skipped question is blank): the server would
 * score against it and write it into the summary. `auth.rules.ROLE_PLACEHOLDER`
 * refuses the same string for flows stored before that (code review F1-1), and
 * `tests/contract/test_role_placeholder_drift.py` pins the two equal.
 */
export const ROLE_PLACEHOLDER = 'your target role';

/**
 * The analyzing screen's checklist (resume-360 ticket 36, widening ticket 09's
 * 4 lines to 7): each line names a real stage of `parse_upload`/
 * `analyze_resume`, never invented busywork — reading/structuring the file,
 * the impact and keyword detectors, the role comparison, fix ranking, the
 * before/after preview build, then final assembly. A 2-page or keyword-dense
 * résumé's `resume_structure` AI call (`docs/adr/0011`) can run 40-60s+; the
 * extra lines give that tail something truthful and specific to hold on,
 * rather than freezing on one generic line for the whole wait.
 */
export const PROCESSING_LINES = ['Reading your resume…', 'Analyzing content and impact…', 'Checking keyword coverage…', 'Comparing against your target role…', 'Identifying your highest-impact fixes…', 'Building your before-and-after preview…', 'Preparing your report…'];
/**
 * The analyzing screen's pacing (resume-360 ticket 09; features/funnel/analyzing.ts):
 * each line runs this long before the next starts, and the finished checklist
 * shows for the beat before the report opens. 7 turns plus the beat are the
 * ~3.1 s minimum dwell (ticket 36 — brought down from 550ms/line so 7 lines
 * don't add a new floor on a fast completion). The prototype's
 * `climbjobs-flow.js` mirrors both.
 */
export const PROCESSING_STEP_MS = 400;
export const CHECKLIST_DONE_BEAT_MS = 300;

/**
 * The analyzing screen's hard ceiling (resume-360 ticket 37): past this many
 * ms of a run without `ready`/`failed`, the poll pauses and the screen shows
 * a "took too long" state with a retry rather than spinning forever. It is a
 * client-side UX ceiling only, independent of and shorter than the server's
 * own retry/budget window (`docs/adr/0011`, `docs/adr/0012`) — a run that
 * finishes after the client gives up is not wasted: retry just resumes
 * polling the same (idempotent) analysis.
 */
export const ANALYSIS_TIMEOUT_MS = 120_000;

/* ---- upload rules (client-side pre-check; the server re-validates ext, size and magic bytes) ---- */
export const MAX_UPLOAD_BYTES = 10 * 1024 * 1024;
export const ACCEPTED = ['pdf', 'docx'];

export function validateFile(file: File): { ok: true; kind: string } | { ok: false; code: 'type' | 'size'; message: string } {
  const ext = (file.name.split('.').pop() || '').toLowerCase();
  if (!ACCEPTED.includes(ext)) return { ok: false, code: 'type', message: 'That looks like a .' + ext + ' file. Please upload a PDF or DOCX.' };
  if (file.size > MAX_UPLOAD_BYTES) return { ok: false, code: 'size', message: 'That file is ' + formatBytes(file.size) + '. The limit is 10MB.' };
  return { ok: true, kind: ext };
}
